import Api from '../api';
import { API_CONFIG } from '../config';

/**
 * Position API Service
 * Handles all position-related API calls
 */

const positionService = {
  /**
   * Get all positions
   * @param {Object} params - Query parameters
   * @param {string} params.$filter - OData filter query
   * @param {string} params.$select - OData select fields
   * @param {string} params.$orderby - OData orderby clause
   * @param {number} params.$top - Limit number of results
   * @param {number} params.$skip - Skip number of results
   * @returns {Promise} Axios response with position data
   */
  async getPositions(params = {}) {
    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...params,
    };

    return Api.get('/data/PositionsV2', { params: queryParams });
  },

  /**
   * Get position by ID
   * @param {string} positionId - Position ID
   * @returns {Promise} Axios response with position data
   */
  async getPositionById(positionId) {
    const params = {
      $filter: `PositionId eq '${positionId}'`,
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    return Api.get('/data/PositionsV2', { params });
  },

  /**
   * Get positions by department
   * @param {string} departmentId - Department ID
   * @returns {Promise} Axios response with position data
   */
  async getPositionsByDepartment(departmentId) {
    const params = {
      $filter: `DepartmentId eq '${departmentId}'`,
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    return Api.get('/data/PositionsV2', { params });
  },

  /**
   * Search positions by title
   * @param {string} searchTerm - Search term for position title
   * @returns {Promise} Axios response with position data
   */
  async searchPositions(searchTerm) {
    const params = {
      $filter: `contains(Title, '${searchTerm}')`,
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    return Api.get('/data/PositionsV2', { params });
  },

  /**
   * Get positions by worker personnel number
   * @param {string} personnelNumber - Worker personnel number
   * @returns {Promise} Axios response with position data
   */
  async getPositionsByWorker(personnelNumber) {
    const params = {
      $filter: `WorkerPersonnelNumber eq '${personnelNumber}'`,
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    return Api.get('/data/PositionsV2', { params });
  },

  /**
   * Create a new position
   * @param {Object} data - Position data
   * @param {string} data.PositionId - Position ID
   * @param {string} data.Description - Position description
   * @param {string} data.PositionTypeId - Position type
   * @param {string} data.PayCycleId - Pay cycle
   * @param {string} data.DepartmentNumber - Department number
   * @param {string} data.JobId - Job ID
   * @returns {Promise} Axios response with created position
   */
  async createPosition(data) {
    const params = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    const headers = {
      'Content-Type': 'application/json',
    };

    return Api.post('/data/PositionsV2', data, { params, headers });
  },

  /**
   * Update position
   * @param {string} positionId - Position ID
   * @param {Object} data - Updated position data
   * @returns {Promise} Axios response with updated position
   */
  async updatePosition(positionId, data) {
    console.log(`📝 Updating position: ${positionId}`, data);

    const params = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    // OData requires specific headers for PATCH
    const headers = {
      'Content-Type': 'application/json',
      'If-Match': '*', // Required for OData updates
    };

    // Remove any OData metadata fields that shouldn't be in the update
    const cleanData = { ...data };
    delete cleanData['@odata.context'];
    delete cleanData['@odata.etag'];

    try {
      // Primary method: PATCH with proper OData key
      const response = await Api.patch(
        `/data/PositionsV2(PositionId='${positionId}')`,
        cleanData,
        { params, headers }
      );
      console.log('✅ Position updated successfully with PATCH');
      return response;
    } catch (error) {
      console.error('❌ PATCH failed:', error.response?.status, error.response?.data?.error?.message);
      throw error;
    }
  },

  /**
   * Update position field
   * @param {string} positionId - Position ID
   * @param {string} fieldName - Field name to update
   * @param {any} value - New value
   * @returns {Promise} Axios response
   */
  async updatePositionField(positionId, fieldName, value) {
    const data = {
      [fieldName]: value,
    };

    return this.updatePosition(positionId, data);
  },

  /**
   * Delete position
   * @param {string} positionId - Position ID
   * @returns {Promise} Axios response
   */
  async deletePosition(positionId) {
    const params = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    const headers = {
      'If-Match': '*',
    };

    return Api.delete(`/data/PositionsV2(PositionId='${positionId}')`, { params, headers });
  },

  /**
   * Get organizational hierarchy for a position
   * Fetches all positions using actual available fields
   * @param {string} personnelNumber - Worker personnel number
   * @returns {Promise} Axios response with all positions
   */
  async getOrganizationalHierarchy(personnelNumber = null) {
    console.log('🔍 Fetching organizational hierarchy for:', personnelNumber);

    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      $top: 200, // Get more positions for better hierarchy
      // Using actual available fields from PositionsV2
      $select: 'PositionId,Description,TitleId,DepartmentNumber,JobId,WorkerPersonnelNumber,WorkerName,WorkerAssignmentStart,WorkerAssignmentEnd,Activation,Retirement',
    };

    try {
      const response = await Api.get('/data/PositionsV2', { params: queryParams });
      console.log(`✅ Fetched ${response.data?.value?.length || 0} positions for hierarchy`);

      // Log the first position to see the data structure
      if (response.data?.value?.length > 0) {
        console.log('📋 Sample position data:', response.data.value[0]);
      }

      return response;
    } catch (error) {
      console.error('❌ Failed to fetch organizational hierarchy:', error);
      console.error('Error details:', error.response?.data);
      throw error;
    }
  },

  /**
   * Get position with full details including worker info
   * @param {string} positionId - Position ID
   * @returns {Promise} Axios response with position details
   */
  async getPositionWithDetails(positionId) {
    const params = {
      $filter: `PositionId eq '${positionId}'`,
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      // Don't use $select - get all available fields
    };

    return Api.get('/data/PositionsV2', { params });
  },
};

export default positionService;
